/**
 * useLinkedChecklistItems Hook
 *
 * Provides access to checklist items that are already linked
 * to a document via the documentChecklistLinks table.
 */

import { api } from "convex/_generated/api";
import { useMemo } from "react";
import type { Id } from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";

export interface LinkedChecklistItem {
  checklistItemId: Id<"checklistItems">;
  auditId: Id<"audits"> | null;
  title: string;
  categoryId: Id<"checklistCategories"> | null;
  categoryName: string;
  clauseRefs: string[];
  exampleDocuments: Array<{ name: string; content: string }>;
  required: boolean;
  linkedAt: number;
}

export function useLinkedChecklistItems(
  documentId: Id<"documents"> | null | undefined,
) {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getLinkedChecklistItems,
    { documentId: documentId ?? null },
    { enabled: !!documentId },
  );

  const items: LinkedChecklistItem[] = useMemo(() => {
    if (!data?.linkedItems) return [];
    return data.linkedItems;
  }, [data]);

  return {
    items,
    isLoading,
    isEmpty: !isLoading && items.length === 0,
  };
}
